import { useState, useEffect, useCallback, useMemo } from "react";
import {
    useUpdateUser,
    useUserTeamContext,
    useUserRoles,
    useNationalTeam,
    useDistriktTeams,
    useSetUserRole,
    useMunicipalitiesForPrimaryTeam,
    type User,
    type UserUpdateRequest,
} from "../api/users";
import { useCurrentUser } from "../api/auth";
import { useRoles } from "../api/roles";
import { useCounties } from "../api/map";
import { filterAssignableRoles, getHighestRole } from "../shared/roleHierarchy";
import { showError, showSuccess } from "../shared/toast";
import "./user-form.css";

const NATIONAL_ROLES = ["SuperAdmin", "RiksAdmin"];
const DISTRIKT_ROLES = ["DistriktAdmin"];
const KRETS_ROLES = ["KretsAdmin", "AvdelningAdmin", "Distributor"];

interface UserFormProps {
    user?: User;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export const UserForm = ({
    user,
    onSuccess,
    onCancel,
}: UserFormProps): JSX.Element => {
    const isEditMode = !!user;
    const [formData, setFormData] = useState<UserUpdateRequest>({
        email: user?.email || "",
        primaryTeamId: user?.primaryTeamId || undefined,
        isApproved: user?.isApproved ?? false,
    });
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [selectedCountyId, setSelectedCountyId] = useState<string | null>(null);
    const [selectedMunicipalityPolygonId, setSelectedMunicipalityPolygonId] = useState<string>("");
    const [errors, setErrors] = useState<Record<string, string>>({});

    const { data: currentUser } = useCurrentUser();
    const { data: currentUserRoles = [] } = useUserRoles(currentUser?.id ?? "");
    const { data: counties = [], isLoading: isLoadingCounties } = useCounties({ enabled: isEditMode });
    const { data: roles = [], isLoading: isLoadingRoles } = useRoles();
    const { data: teamContext, isLoading: isLoadingTeamContext } = useUserTeamContext(user?.id ?? "");
    const { data: nationalTeam } = useNationalTeam();

    const isEditingSelf = isEditMode && !!user && !!currentUser && user.id === currentUser.id;
    const currentUserHighestRole = useMemo(
        () => getHighestRole(currentUserRoles.map((r) => r.role)),
        [currentUserRoles]
    );
    const assignableRoles = useMemo(
        () =>
            currentUserHighestRole
                ? filterAssignableRoles(roles, currentUserHighestRole)
                : roles.filter((r) => r.value === "Distributor"),
        [roles, currentUserHighestRole]
    );
    const canAssignCurrentRole = useMemo(
        () =>
            teamContext?.role &&
            assignableRoles.some((r) => r.value === teamContext.role),
        [teamContext?.role, assignableRoles]
    );
    const { data: distriktTeams = [] } = useDistriktTeams(selectedCountyId);
    const { data: municipalities = [], isLoading: isLoadingMunicipalities } = useMunicipalitiesForPrimaryTeam(
        selectedCountyId ?? undefined
    );

    const updateMutation = useUpdateUser();
    const setRoleMutation = useSetUserRole();

    const showCounty = DISTRIKT_ROLES.includes(selectedRole) || KRETS_ROLES.includes(selectedRole);
    const showKrets = KRETS_ROLES.includes(selectedRole);

    const handleCountyChange = useCallback((countyId: string | null) => {
        setSelectedCountyId(countyId);
        setSelectedMunicipalityPolygonId("");
    }, []);

    // Initialize form from team context when editing
    useEffect(() => {
        if (!teamContext || !user) return;
        setSelectedRole(teamContext.role);
        if (teamContext.countyId) {
            setSelectedCountyId(teamContext.countyId);
        }
        if (teamContext.municipalityPolygonId) {
            setSelectedMunicipalityPolygonId(teamContext.municipalityPolygonId);
        }
    }, [teamContext, user]);

    useEffect(() => {
        if (user) {
            setFormData({
                email: user.email,
                primaryTeamId: user.primaryTeamId || undefined,
                isApproved: user.isApproved,
            });
        }
    }, [user]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Ogiltig e-postadress";
        }

        if (showCounty && !selectedCountyId) {
            newErrors.county = "Välj län";
        }

        if (showKrets && (!selectedCountyId || !selectedMunicipalityPolygonId)) {
            newErrors.krets = "Välj län och krets (kommun)";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getTeamIdForSave = (): string | null => {
        if (NATIONAL_ROLES.includes(selectedRole) && nationalTeam) {
            return nationalTeam.id;
        }
        if (DISTRIKT_ROLES.includes(selectedRole) && distriktTeams.length > 0) {
            return distriktTeams[0].id;
        }
        if (KRETS_ROLES.includes(selectedRole) && selectedMunicipalityPolygonId) {
            const m = municipalities.find((x) => x.municipalityPolygonId === selectedMunicipalityPolygonId);
            return m?.teamId ?? null;
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isEditMode || !user) {
            showError("Kan endast redigera befintliga användare");
            return;
        }

        if (!validate()) {
            return;
        }

        const teamId = isEditingSelf || !canAssignCurrentRole ? null : getTeamIdForSave();
        if (!isEditingSelf && canAssignCurrentRole && !teamId) {
            showError("Kunde inte bestämma team för vald roll");
            return;
        }

        try {
            await updateMutation.mutateAsync({
                id: user.id,
                email: formData.email || undefined,
                isApproved: formData.isApproved,
            });

            if (!isEditingSelf && canAssignCurrentRole && teamId) {
                await setRoleMutation.mutateAsync({
                    id: user.id,
                    role: selectedRole,
                    teamId,
                });
            }

            onSuccess?.();
            showSuccess("Användaren har uppdaterats");
        } catch (error: unknown) {
            const err = error as { response?: { data?: { errors?: Record<string, string>; message?: string } } };
            if (err?.response?.data?.errors) {
                setErrors(err.response.data.errors);
            } else {
                const message = err?.response?.data?.message || "Ett fel uppstod";
                showError("Kunde inte spara användaren", message);
            }
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setSelectedRole(value);
        if (!DISTRIKT_ROLES.includes(value) && !KRETS_ROLES.includes(value)) {
            setSelectedCountyId(null);
            setSelectedMunicipalityPolygonId("");
        }
        setErrors((prev) => {
            const next = { ...prev };
            delete next.county;
            delete next.krets;
            return next;
        });
    };

    const handleKretsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedMunicipalityPolygonId(e.target.value);
        if (errors.krets) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.krets;
                return newErrors;
            });
        }
    };

    const isLoading = updateMutation.isPending || setRoleMutation.isPending;

    if (!isEditMode) {
        return (
            <div className="user-form-overlay">
                <div className="user-form">
                    <h3>Skapa användare</h3>
                    <p>Användare måste registrera sig själva. Använd registreringsflödet för att skapa nya användare.</p>
                    <button
                        type="button"
                        className="form-button form-button--secondary"
                        onClick={onCancel}
                    >
                        Stäng
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="user-form-overlay">
            <div className="user-form">
                <form onSubmit={handleSubmit}>
                    <h3>Redigera användare</h3>

                    <div className="form-group">
                        <label htmlFor="userName">Användarnamn</label>
                        <input
                            id="userName"
                            type="text"
                            value={user.userName}
                            disabled
                            className="disabled-field"
                        />
                        <p className="form-hint">Användarnamn kan inte ändras</p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">E-post</label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email || ""}
                            onChange={handleChange}
                            aria-invalid={!!errors.email}
                            aria-describedby={errors.email ? "email-error" : undefined}
                            className={errors.email ? "error-field" : ""}
                        />
                        {errors.email && (
                            <span id="email-error" role="alert" className="error-message">
                                {errors.email}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="role">Roll</label>
                        {!canAssignCurrentRole && teamContext?.role ? (
                            <>
                                <input
                                    id="role"
                                    type="text"
                                    value={teamContext.role}
                                    disabled
                                    className="disabled-field"
                                />
                                <p className="form-hint">
                                    Du har inte behörighet att ändra denna roll.
                                </p>
                            </>
                        ) : (
                            <>
                                <select
                                    id="role"
                                    value={selectedRole}
                                    onChange={handleRoleChange}
                                    disabled={isLoadingRoles || isLoadingTeamContext || isEditingSelf}
                                >
                                    <option value="">Välj roll...</option>
                                    {assignableRoles.map((role) => (
                                        <option key={role.value} value={role.value}>
                                            {role.name}
                                        </option>
                                    ))}
                                </select>
                                {isEditingSelf && (
                                    <p className="form-hint">Du kan inte ändra din egen roll.</p>
                                )}
                            </>
                        )}
                    </div>

                    {showCounty && !isEditingSelf && canAssignCurrentRole && (
                        <div className="form-group">
                            <label htmlFor="county">Län</label>
                            <select
                                id="county"
                                value={selectedCountyId ?? ""}
                                onChange={(e) => handleCountyChange(e.target.value || null)}
                                disabled={isLoadingCounties}
                                className={errors.county ? "error-field" : ""}
                            >
                                <option value="">Välj län...</option>
                                {counties.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                            {errors.county && (
                                <span role="alert" className="error-message">
                                    {errors.county}
                                </span>
                            )}
                        </div>
                    )}

                    {showKrets && !isEditingSelf && canAssignCurrentRole && (
                        <div className="form-group">
                            <label htmlFor="krets">Krets</label>
                            <select
                                id="krets"
                                value={selectedMunicipalityPolygonId}
                                onChange={handleKretsChange}
                                disabled={isLoadingMunicipalities}
                                className={errors.krets ? "error-field" : ""}
                            >
                                <option value="">
                                    {selectedCountyId
                                        ? isLoadingMunicipalities
                                            ? "Laddar kommuner..."
                                            : "Välj krets..."
                                        : "Välj län först..."}
                                </option>
                                {municipalities.map((m) => (
                                    <option key={m.municipalityPolygonId} value={m.municipalityPolygonId}>
                                        {m.name} ({m.code})
                                    </option>
                                ))}
                            </select>
                            {errors.krets && (
                                <span role="alert" className="error-message">
                                    {errors.krets}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="isApproved" className="checkbox-label">
                            <input
                                id="isApproved"
                                name="isApproved"
                                type="checkbox"
                                checked={formData.isApproved ?? false}
                                onChange={handleChange}
                            />
                            <span>Godkänd</span>
                        </label>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="form-button form-button--secondary"
                            onClick={onCancel}
                            disabled={isLoading}
                        >
                            Avbryt
                        </button>
                        <button
                            type="submit"
                            className="form-button form-button--primary"
                            disabled={isLoading}
                        >
                            {isLoading ? "Sparar..." : "Spara"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
